import React, { useContext, useRef, CSSProperties, ReactNode } from "react";
import { DataContext } from "../../lib/contexts";
import { CloseOutlined } from "@ant-design/icons";

interface MInputProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onPressEnter?: () => void;
  placeholder?: string;
  type?: string;
  size?: "large" | "default";
  allowClear?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  className?: string;
  style?: CSSProperties;
  autoFocus?: boolean;
  disabled?: boolean;
  innerref?: React.RefObject<HTMLInputElement | null>;
}

const baseWrapperStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  background: "#f5f5f5",
  borderRadius: 14,
  padding: "0 12px",
  height: 40,
  fontSize: 16,
  flex: 1,
  minWidth: 0,
};

const inputStyle: CSSProperties = {
  flex: 1,
  border: "none",
  outline: "none",
  background: "transparent",
  fontSize: "inherit",
  fontFamily: "inherit",
  minWidth: 0,
  padding: 0,
};

const clearBtnStyle: CSSProperties = {
  cursor: "pointer",
  color: "#9ca3af",
  fontSize: 12,
  display: "flex",
  padding: 4,
};

export function MInput(props: MInputProps) {
  const { setIsInputting } = useContext(DataContext);
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = props.innerref || internalRef;

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && props.onPressEnter) {
      props.onPressEnter();
    }
  }

  function handleClear() {
    if (props.onChange) {
      const nativeEvent = new Event("input", { bubbles: true });
      const syntheticEvent = {
        target: { value: "" },
        currentTarget: { value: "" },
        nativeEvent,
      } as React.ChangeEvent<HTMLInputElement>;
      props.onChange(syntheticEvent);
    }
    ref.current?.focus();
  }

  const height = props.size === "large" ? 44 : 40;

  return (
    <div
      className={props.className}
      style={{
        ...baseWrapperStyle,
        height,
        ...props.style,
      }}
    >
      {props.prefix}
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        type={props.type || "text"}
        value={props.value ?? ""}
        placeholder={props.placeholder}
        autoFocus={props.autoFocus}
        disabled={props.disabled}
        style={inputStyle}
        onChange={props.onChange}
        onKeyDown={handleKeyDown}
        onFocus={(e) => {
          setIsInputting(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsInputting(false);
          props.onBlur?.(e);
        }}
      />
      {props.allowClear && props.value && (
        <span style={clearBtnStyle} onClick={handleClear}>
          <CloseOutlined />
        </span>
      )}
      {props.suffix}
    </div>
  );
}
